import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { EventManager } from 'app/services/events/events.service';
import { GdrivesService } from 'app/services/gdrives/gdrives.service';
import {
  DEFAULT_GOOGLE_AVATAR_URI,
  TagDatamart,
  Rating,
  File,
  PrimeFileNode,
  Contribution,
  People,
  User,
  Workflow,
  WorkflowState,
} from 'app/types';
import {
  getFileContributors,
  isFileFlagged,
  isFileVerified,
} from 'app/utils/utils';

@Component({
  selector: 'app-ng-table',
  templateUrl: './ng-table.component.html',
  styleUrl: './ng-table.component.scss',
})
export class NgTableComponent implements OnInit, OnChanges {
  @Input() mode: string = 'documents';
  @Input() documents: Array<PrimeFileNode> = new Array<PrimeFileNode>();
  @Input() peoples: Array<Contribution> = new Array<Contribution>();
  @Input() canSelect: boolean = true;
  @Input() showApprovers: boolean = false;
  @Input() showLastAction: boolean = false;
  @Input() sortedByLastModified: boolean = true;
  @Input() workflows: Map<string, Workflow> = new Map<string, Workflow>();

  readonly workflowState = WorkflowState;
  readonly maximalAvatar: number = 3;
  maximalMarkPossible: number = 5;
  selectedFiles: Array<PrimeFileNode> = new Array<PrimeFileNode>();
  readonly default_avatar_uri: string = DEFAULT_GOOGLE_AVATAR_URI;

  constructor(private gdriveService: GdrivesService) {}

  async ngOnChanges() {
    this.documents.forEach((file) => {
      file.averageRating = this.getRatingAverage(file);
    });
  }

  async ngOnInit() {
    EventManager.on('clearTableSelection', () => {
      this.selectedFiles = new Array<PrimeFileNode>();
      this.sendSelectedFiles();
    });
  }

  async sendSelectedFiles() {
    this.documents?.forEach((file: PrimeFileNode) => {
      // Check if the file is selected
      if (
        this.selectedFiles.find(
          (selectedFile: PrimeFileNode) => selectedFile.id === file.id,
        )
      ) {
        file.selectedRow = 'selected-row';
      } else {
        file.selectedRow = '';
      }
    });
    this.gdriveService.setTableSelectedFile(this.selectedFiles);
    // Generate a custom event to notify the pages component that the selected files have changed
    EventManager.emit('selectedFilesChanged', this.selectedFiles);
  }

  getRatingAverage(file: File): number {
    let sum: number = 0;

    file?.ratings?.forEach((rating: Rating) => {
      sum += rating.rating;
    });
    return file?.ratings?.length ? sum / file?.ratings.length : 0;
  }

  getTooltipTags(file: File): string {
    let tags: string = '';

    file?.tags?.forEach((tag: TagDatamart) => {
      tags += tag?.tag?.label + ', ';
    });
    return tags.slice(0, -2);
  }

  getAvatarImage(contrib: People): string {
    return contrib?.photo_uri ?? this.default_avatar_uri;
  }

  getContributorName(contrib: People): string {
    return contrib?.fullname ?? contrib?.name ?? 'Unknown';
  }

  getContributorEmail(contrib: People): string {
    return contrib?.email ?? 'Unknown';
  }

  getContributorTooltip(contrib: People): string {
    const name = this.getContributorName(contrib);

    if (contrib?.title) {
      return `${name}\n\n${contrib.title}`;
    }

    return name;
  }

  getContributorsTooltip(contributors: People[]): string {
    return contributors
      .slice(this.maximalAvatar, contributors.length)
      .map((pi) => this.getContributorName(pi))
      .join('\n');
  }

  getContributors(
    file: File,
  ): { contribs: People[]; count: number; names: string } | undefined {
    const people = getFileContributors(file);

    return people;
  }

  getWorkflowContributors(user: User): string {
    let name = this.getContributorTooltip(user.people);

    if (name.toLowerCase() === 'unknown') {
      name = user.primary_email;
    }
    return name;
  }

  isVerified(file: File): boolean {
    return isFileVerified(file);
  }

  isFlagged(file: File): boolean {
    return isFileFlagged(file);
  }
}
